<template>
  <page-header-wrapper>
    <a-card ref="memberCard" :bordered="false">
      <a-tabs v-model="tab" @change="changeTab">
        <a-tab-pane key="users" tab="用户列表" />
        <a-tab-pane key="certifications" tab="企业认证" />
        <a-tab-pane key="cancellations" tab="用户注销" />
      </a-tabs>

      <div ref="toolbar" class="toolbar">
        <a-button v-if="tab==='cancellations'" @click="cancelReasonsVisible=true">注销原因配置</a-button>
        <a-select v-if="tab === 'certifications'" v-model="query.auditStatus" allow-clear placeholder="审核状态（全部）" style="width:180px" @change="search">
          <a-select-option :value="1">待审核</a-select-option>
          <a-select-option :value="2">已通过</a-select-option>
          <a-select-option :value="3">已驳回</a-select-option>
        </a-select>
        <a-input-search v-model="query.keyword" allow-clear :placeholder="searchPlaceholder" style="width:380px" @search="search" />
        <a-input-search v-if="tab === 'users'" v-model="query.country" allow-clear placeholder="国家中文名 / 英文名" style="width:220px" @search="search" />
        <a-input-search v-if="tab === 'users'" v-model="query.companyName" allow-clear placeholder="企业名称（支持模糊搜索）" style="width:260px" @search="search" />
        <a-select v-if="tab === 'users'" v-model="query.sortBy" allow-clear placeholder="消费 / 注册时间排序" style="width:200px" @change="search">
          <a-select-option value="orderCount">消费订单数量</a-select-option><a-select-option value="paidAmount">成交金额（人民币）</a-select-option><a-select-option value="paidAmountUsd">成交金额（美元）</a-select-option><a-select-option value="registerTime">注册时间</a-select-option>
        </a-select>
        <a-radio-group v-if="tab === 'users'" v-model="query.sortOrder" :disabled="!query.sortBy" @change="search"><a-radio-button value="asc">升序</a-radio-button><a-radio-button value="desc">降序</a-radio-button></a-radio-group>
      </div>

      <div ref="tableRegion" class="member-table-region">
      <a-table
        v-if="tab === 'users'"
        class="member-users-table"
        key="users"
        row-key="id"
        bordered
        :loading="loading"
        :columns="userColumns"
        :data-source="rows"
        :pagination="false"
        :scroll="{ x: userTableWidth, y: tableBodyHeight }">
        <template slot="user" slot-scope="text, record">
          <dk-member-identity :record="record" />
        </template>
        <template slot="contact" slot-scope="text, record">
          <div>{{ record.area_code || '' }} {{ record.mobile || '-' }}</div>
          <div class="muted">{{ record.email || '-' }}</div>
        </template>
        <template slot="cert" slot-scope="text, record">
          <a-tag :color="certColor(record)">{{ record.cert_status_text || '未认证' }}</a-tag>
          <div class="muted">默认企业：{{ record.default_enterprise_name || '-' }}</div>
          <div class="muted">认证姓名：{{ record.cert_real_name || '-' }}</div>
          <div class="muted">认证手机：{{ record.cert_phone_text || '-' }}</div>
          <div class="muted">认证邮箱：{{ record.cert_email_text || '-' }}</div>
          <a v-if="record.cert_aid" @click="openCert(record.cert_aid)">查看/编辑认证</a>
          <span v-else class="muted">暂无认证资料</span>
        </template>
        <template slot="address" slot-scope="text, record">
          <div>{{ record.default_address || '-' }}</div>
          <div class="muted" v-if="record.address_name">{{ record.address_name }} {{ record.address_mobile }}</div>
        </template>
        <template slot="service" slot-scope="text, record">
          <div class="user" v-if="record.service_id">
            <a-avatar :src="record.service_avatar" icon="customer-service" :size="28" />
            <div>
              <div>{{ record.service_nickname || '-' }}</div>
              <div class="muted">{{ record.service_mobile || '-' }} · ID {{ record.service_id }}</div>
            </div>
          </div>
          <span v-else class="muted">未分配</span>
          <div><a @click="openAssign(record)">更换客服</a></div>
        </template>
        <template slot="brochure" slot-scope="text, record">
          <a-tag :color="brochureColor(record)">
            {{ record.brochure_status_text || '无权益' }}
          </a-tag>
          <div><a @click="openBrochure(record)">设置权益</a></div>
        </template>
        <template slot="spend" slot-scope="text, record">
          <div title="正式订单数包含待付款、已付款及已取消订单；不含询盘、已删除和隐藏订单。成交金额为实际收款扣除已完成退款，两种币种分别统计。">订单 {{ record.order_count || 0 }} / 已付款 {{ record.paid_order_count || 0 }}</div>
          <div class="muted">成交 ¥{{ money(record.paid_total) }}</div>
          <div class="muted">成交 ${{ money(record.paid_total_usd) }}</div>
        </template>
        <template slot="status" slot-scope="value">
          <a-tag :color="Number(value) === 1 ? 'green' : 'red'">{{ Number(value) === 1 ? '正常' : '禁用' }}</a-tag>
        </template>
        <template slot="userOp" slot-scope="text, record">
          <a @click="openUser(record)">详情</a>
          <a-divider type="vertical" />
          <a-popconfirm :title="Number(record.status) === 1 ? '确定禁用？' : '确定启用？'" @confirm="toggleUser(record)">
            <a>{{ Number(record.status) === 1 ? '禁用' : '启用' }}</a>
          </a-popconfirm>
        </template>
      </a-table>

      <a-table
        v-else-if="tab === 'certifications'"
        key="certifications"
        row-key="aid"
        bordered
        :loading="loading"
        :columns="certColumns"
        :data-source="rows"
        :pagination="false"
        :scroll="{ x: 1400, y: tableBodyHeight }">
        <template slot="type" slot-scope="text, record"><a-tag>{{ record.type_text || '国内版' }}</a-tag></template>
        <template slot="audit" slot-scope="text, record">
          <a-tag :color="auditColor(record.to_examine)">{{ record.to_examine_text || auditText(record.to_examine) }}</a-tag>
        </template>
        <template slot="images" slot-scope="text, record">
          <a-badge :count="record.image_count || 0" :number-style="{ backgroundColor: '#52c41a' }">
            <a @click="openCert(record.aid)">查看证件图</a>
          </a-badge>
        </template>
        <template slot="certOp" slot-scope="text, record">
          <a @click="openCert(record.aid)">详情/编辑</a>
          <a-divider type="vertical" />
          <a @click="audit(record, 2)">通过</a>
          <a-divider type="vertical" />
          <a @click="audit(record, 1)">驳回</a>
        </template>
      </a-table>

      <a-table v-else key="cancellations" row-key="aid" bordered :loading="loading" :columns="cancelColumns" :data-source="rows" :pagination="false" :scroll="{ x: 1400, y: tableBodyHeight }" />
      <a-drawer title="注销原因配置" :visible="cancelReasonsVisible" width="85%" @close="cancelReasonsVisible=false"><dk-legacy v-if="cancelReasonsVisible" module-override="cancel-reason" /></a-drawer>
      </div>

      <a-pagination
        ref="pager"
        class="pager"
        show-size-changer
        show-quick-jumper
        :current="query.pageNum"
        :page-size="query.pageSize"
        :total="total"
        :show-total="n => `共 ${n} 条`"
        @change="changePage"
        @showSizeChange="changePage" />
    </a-card>

    <a-drawer title="会员详情" :visible="userVisible" width="720" @close="userVisible = false">
      <template v-if="userDetail">
        <a-descriptions bordered :column="2" size="small">
          <a-descriptions-item label="头像" :span="2"><a-avatar :src="userDetail.avatar" :size="64" icon="user" /></a-descriptions-item>
          <a-descriptions-item label="ID">{{ userDetail.id }}</a-descriptions-item>
          <a-descriptions-item label="昵称">{{ userDetail.nickname }}</a-descriptions-item>
          <a-descriptions-item label="手机">{{ userDetail.area_code }} {{ userDetail.mobile }}</a-descriptions-item>
          <a-descriptions-item label="邮箱">{{ userDetail.email || '-' }}</a-descriptions-item>
          <a-descriptions-item label="国家">{{ userDetail.country || '-' }}</a-descriptions-item>
          <a-descriptions-item label="认证状态">{{ userDetail.cert_status_text }}</a-descriptions-item>
          <a-descriptions-item label="默认企业">{{ userDetail.default_enterprise_name || '-' }}</a-descriptions-item>
          <a-descriptions-item label="认证姓名">{{ userDetail.cert_real_name || '-' }}</a-descriptions-item>
          <a-descriptions-item label="认证手机">{{ userDetail.cert_phone_text || '-' }}</a-descriptions-item>
          <a-descriptions-item label="认证邮箱">{{ userDetail.cert_email_text || '-' }}</a-descriptions-item>
          <a-descriptions-item label="默认地址" :span="2">{{ userDetail.default_address || '-' }}</a-descriptions-item>
          <a-descriptions-item label="专属客服">{{ userDetail.service_nickname || '未分配' }} {{ userDetail.service_mobile || '' }}</a-descriptions-item>
          <a-descriptions-item label="画册权益">{{ userDetail.brochure_status_text }}</a-descriptions-item>
          <a-descriptions-item label="消费">订单 {{ userDetail.order_count || 0 }} / 已付款 {{ userDetail.paid_order_count || 0 }}<div>成交 ¥{{ money(userDetail.paid_total) }} / ${{ money(userDetail.paid_total_usd) }}</div></a-descriptions-item>
          <a-descriptions-item label="最后登录">{{ userDetail.last_login_time_text }}</a-descriptions-item>
        </a-descriptions>
        <a-divider>认证企业信息</a-divider>
        <div v-if="userDetail.cert_aid || userDetail.default_enterprise_name">
          <p>默认企业：{{ userDetail.default_enterprise_name || userDetail.cert_enterprise_name || '-' }}</p>
          <p>认证姓名：{{ userDetail.cert_real_name || userDetail.cert_contact_name || '-' }}</p>
          <p>认证手机：{{ userDetail.cert_phone_text || userDetail.cert_contact_phone || '-' }}</p>
          <p>认证邮箱：{{ userDetail.cert_email_text || userDetail.cert_email || '-' }}</p>
          <a v-if="userDetail.cert_aid" @click="openCert(userDetail.cert_aid)">打开认证详情/编辑</a>
        </div>
        <a-empty v-else description="暂无企业认证资料" />
      </template>
    </a-drawer>

    <a-modal title="更换专属客服" :visible="assignVisible" :confirm-loading="saving" @ok="saveAssign" @cancel="assignVisible = false">
      <a-select v-model="assignServiceId" show-search option-filter-prop="children" style="width:100%" placeholder="选择专属客服" allow-clear>
        <a-select-option :value="0">清除专属客服</a-select-option>
        <a-select-option v-for="item in agents" :key="item.id" :value="item.id">
          {{ item.real_name || item.nickname }} / {{ item.mobile || '-' }} / ID {{ item.id }}
        </a-select-option>
      </a-select>
    </a-modal>

    <a-modal title="画册权益设置" :visible="brochureVisible" :confirm-loading="saving" width="600px" @ok="saveBrochure" @cancel="brochureVisible = false">
      <a-form-model :label-col="{ span: 8 }" :wrapper-col="{ span: 14 }">
        <section class="brochure-right-section" aria-label="画册查看权益">
          <h4>查看权益</h4>
          <a-form-model-item label="可查看画册"><a-switch v-model="brochureForm.viewBrochure" aria-label="允许查看画册" /></a-form-model-item>
          <a-form-model-item label="查看永久有效"><a-switch v-model="brochureForm.viewPermanent" :disabled="!brochureForm.viewBrochure" aria-label="查看永久有效" /></a-form-model-item>
          <a-form-model-item v-if="!brochureForm.viewPermanent" label="查看到期时间">
            <a-date-picker :value="expiryDate(brochureForm.duration)" :disabled="!brochureForm.viewBrochure" placeholder="选择查看到期日期和时间" show-time format="YYYY-MM-DD HH:mm:ss" style="width:100%" @change="v => brochureForm.duration = v ? v.unix() : 0" />
          </a-form-model-item>
        </section>
        <section class="brochure-right-section" aria-label="画册下载权益">
          <h4>下载权益</h4>
          <a-form-model-item label="可下载画册"><a-switch v-model="brochureForm.downloadBrochure" aria-label="允许下载画册" /></a-form-model-item>
          <a-form-model-item label="下载永久有效"><a-switch v-model="brochureForm.downloadPermanent" :disabled="!brochureForm.downloadBrochure" aria-label="下载永久有效" /></a-form-model-item>
          <a-form-model-item v-if="!brochureForm.downloadPermanent" label="下载到期时间">
            <a-date-picker :value="expiryDate(brochureForm.downloadDuration)" :disabled="!brochureForm.downloadBrochure" placeholder="选择下载到期日期和时间" show-time format="YYYY-MM-DD HH:mm:ss" style="width:100%" @change="v => brochureForm.downloadDuration = v ? v.unix() : 0" />
          </a-form-model-item>
        </section>
      </a-form-model>
    </a-modal>

    <a-drawer title="企业认证详情" :visible="certVisible" width="860" @close="certVisible = false">
      <template v-if="certDetail">
        <a-alert :message="`${certDetail.type_text || '国内版'} · ${certDetail.to_examine_text || ''}`" type="info" show-icon style="margin-bottom:16px" />
        <a-form-model :model="certForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-row>
            <a-col :span="12"><a-form-model-item label="企业名称"><a-input v-model="certForm.enterprise_name" /></a-form-model-item></a-col>
            <a-col :span="12"><a-form-model-item label="认证类型"><a-select v-model="certForm.type"><a-select-option value="1">国内版</a-select-option><a-select-option value="2">国际版</a-select-option></a-select></a-form-model-item></a-col>
            <a-col :span="12"><a-form-model-item label="联系人"><a-input v-model="certForm.contact_name" /></a-form-model-item></a-col>
            <a-col :span="12"><a-form-model-item label="手机号"><a-input v-model="certForm.contact_phone" /></a-form-model-item></a-col>
            <a-col :span="12"><a-form-model-item label="邮箱"><a-input v-model="certForm.e_mail" /></a-form-model-item></a-col>
            <a-col :span="12"><a-form-model-item label="职务"><a-input v-model="certForm.post" /></a-form-model-item></a-col>
            <template v-if="String(certForm.type) !== '2'">
              <a-col :span="12"><a-form-model-item label="统一社会信用码"><a-input v-model="certForm.unified_social_credit_code" /></a-form-model-item></a-col>
              <a-col :span="12"><a-form-model-item label="法人姓名"><a-input v-model="certForm.legal_representative_name" /></a-form-model-item></a-col>
              <a-col :span="24"><a-form-model-item label="注册地址" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }"><a-input v-model="certForm.company_registered_address" /></a-form-model-item></a-col>
              <a-col :span="12"><a-form-model-item label="对公账号"><a-input v-model="certForm.corporate_account" /></a-form-model-item></a-col>
              <a-col :span="12"><a-form-model-item label="开户行"><a-input v-model="certForm.fnotbwtaio" /></a-form-model-item></a-col>
            </template>
            <template v-else>
              <a-col :span="12"><a-form-model-item label="WhatsApp"><a-input v-model="certForm.WhatsApp" /></a-form-model-item></a-col>
              <a-col :span="12"><a-form-model-item label="开户人"><a-input v-model="certForm.account_holder" /></a-form-model-item></a-col>
              <a-col :span="24"><a-form-model-item label="银行地址" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }"><a-input v-model="certForm.bank_address" /></a-form-model-item></a-col>
              <a-col :span="24"><a-form-model-item label="开户人地址" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }"><a-input v-model="certForm.account_holders_address" /></a-form-model-item></a-col>
              <a-col :span="12"><a-form-model-item label="电报码"><a-input v-model="certForm.telegram_code" /></a-form-model-item></a-col>
            </template>
            <a-col :span="24"><a-form-model-item label="官网" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }"><a-input v-model="certForm.official_website" /></a-form-model-item></a-col>
          </a-row>
        </a-form-model>
        <a-divider>上传证件（点击预览，核对左侧录入信息）</a-divider>
        <a-row :gutter="12">
          <a-col v-for="img in (certDetail.images || [])" :key="img.field" :span="8" style="margin-bottom:12px">
            <div class="img-card" @click="previewImage(img.url)">
              <img :src="img.url" :alt="img.label" />
              <div>{{ img.label }}</div>
            </div>
          </a-col>
          <a-col v-if="!(certDetail.images && certDetail.images.length)" :span="24"><a-empty description="暂无图片" /></a-col>
        </a-row>
        <div style="margin-top:16px;text-align:right">
          <a-button @click="certVisible = false">关闭</a-button>
          <a-button type="primary" :loading="saving" style="margin-left:8px" @click="saveCert">保存资料</a-button>
          <a-button type="primary" ghost style="margin-left:8px" @click="audit({ aid: certDetail.aid }, 2)">通过</a-button>
          <a-button type="danger" ghost style="margin-left:8px" @click="audit({ aid: certDetail.aid }, 1)">驳回</a-button>
        </div>
      </template>
    </a-drawer>

    <a-modal :visible="previewVisible" :footer="null" @cancel="previewVisible = false" width="720px">
      <img v-if="previewUrl" :src="previewUrl" style="width:100%" />
    </a-modal>
  </page-header-wrapper>
</template>

<script>
import {
  pageMembers, getMember, assignMemberService, setMemberBrochure,
  pageCertifications, getCertification, updateCertification,
  pageCancellations, setMemberStatus, auditCertification
} from '@/api/dkmall/member'
import { listServiceAgents } from '@/api/dkmall/customer-service'
import moment from 'moment'
import DkLegacy from '@/views/dkmall/legacy'
import DkMemberIdentity from '@/components/DkMemberIdentity'

export default {
  name: 'DkMallMember',
  components: { DkMemberIdentity, DkLegacy },
  props: { initialTab: { type: String, default: 'users' } },
  data () {
    return {
      cancelReasonsVisible:false,
      tab: ['users','certifications','cancellations'].includes(this.initialTab) ? this.initialTab : 'users',
      rows: [],
      total: 0,
      loading: false,
      tableBodyHeight: 240,
      saving: false,
      query: { keyword: '', country: '', companyName: '', auditStatus: undefined, sortBy: undefined, sortOrder: 'desc', pageNum: 1, pageSize: 20 },
      userVisible: false,
      userDetail: null,
      assignVisible: false,
      assignUserId: null,
      assignServiceId: undefined,
      agents: [],
      brochureVisible: false,
      brochureUserId: null,
      brochureForm: { viewBrochure: false, downloadBrochure: false, viewPermanent: false, downloadPermanent: false, duration: 0, downloadDuration: 0 },
      certVisible: false,
      certDetail: null,
      certForm: {},
      previewVisible: false,
      previewUrl: '',
      userColumns: [
        { title: '用户(头像/昵称/ID)', scopedSlots: { customRender: 'user' }, width: 200, className: 'member-identity-column' },
        { title: '注册手机/邮箱', scopedSlots: { customRender: 'contact' }, width: 170 },
        { title: '所属国家', dataIndex: 'country', width: 90 },
        { title: '企业认证(状态/默认企业/姓名/手机邮箱)', scopedSlots: { customRender: 'cert' }, width: 260 },
        { title: '默认收货地址', scopedSlots: { customRender: 'address' }, width: 220 },
        { title: '专属客服', scopedSlots: { customRender: 'service' }, width: 200 },
        { title: '画册权益', scopedSlots: { customRender: 'brochure' }, width: 220, className: 'member-brochure-column' },
        { title: '消费统计', scopedSlots: { customRender: 'spend' }, width: 200 },
        { title: '最后登录', dataIndex: 'last_login_time_text', width: 160 },
        { title: '状态', dataIndex: 'status', scopedSlots: { customRender: 'status' }, width: 80 },
        { title: '操作', scopedSlots: { customRender: 'userOp' }, width: 120, className: 'member-operation-column' }
      ],
      certColumns: [
        { title: 'ID', dataIndex: 'aid', width: 70 },
        { title: '类型', scopedSlots: { customRender: 'type' }, width: 90 },
        { title: '企业名称', dataIndex: 'enterprise_name', width: 200 },
        { title: '联系人', dataIndex: 'contact_name', width: 100 },
        { title: '手机号', dataIndex: 'contact_phone', width: 130 },
        { title: '邮箱', dataIndex: 'e_mail', width: 180 },
        { title: '审核状态', scopedSlots: { customRender: 'audit' }, width: 100 },
        { title: '证件图', scopedSlots: { customRender: 'images' }, width: 120 },
        { title: '失败原因', dataIndex: 'reason_for_failure' },
        { title: '操作', scopedSlots: { customRender: 'certOp' }, width: 200, fixed: 'right' }
      ],
      cancelColumns: [
        { title: 'ID', dataIndex: 'aid', width: 70 },
        { title: '用户ID', dataIndex: 'user_id', width: 90 },
        { title: '昵称', dataIndex: 'nickname', width: 130 },
        { title: '手机号', dataIndex: 'mobile', width: 150 },
        { title: '邮箱', dataIndex: 'email', width: 200 },
        { title: '注销类型', dataIndex: 'cancel_type', width: 120 },
        { title: '注销原因', dataIndex: 'cancel_reason' },
        { title: '创建时间', dataIndex: 'create_time', width: 160 }
      ]
    }
  },
  computed: {
    userTableWidth () { return this.userColumns.reduce((width, column) => width + column.width, 0) },
    searchPlaceholder () {
      if (this.tab === 'certifications') return '企业名、联系人、手机、邮箱或用户ID'
      return '昵称、姓名、手机号、邮箱或ID'
    }
  },
  watch: {
    '$route.query' () { if(this.applyCertificationRoute())this.load() }
  },
  created () { this.applyCertificationRoute();this.load() },
  mounted () {
    window.addEventListener('resize', this.scheduleTableHeight)
    if (window.ResizeObserver) {
      this.tableResizeObserver = new ResizeObserver(this.scheduleTableHeight)
      this.tableResizeObserver.observe(this.$refs.memberCard.$el)
      this.tableResizeObserver.observe(this.$refs.toolbar)
      this.tableResizeObserver.observe(this.$refs.pager.$el)
      // The surrounding layout can change when navigation, tabs or breadcrumbs wrap.
      this.tableResizeObserver.observe(this.$el.parentElement)
    }
    this.scheduleTableHeight()
  },
  activated () { this.scheduleTableHeight() },
  updated () { this.scheduleTableHeight() },
  deactivated () { cancelAnimationFrame(this.tableHeightFrame) },
  beforeDestroy () {
    window.removeEventListener('resize', this.scheduleTableHeight)
    if (this.tableResizeObserver) this.tableResizeObserver.disconnect()
    cancelAnimationFrame(this.tableHeightFrame)
  },
  methods: {
    applyCertificationRoute () {
      const query=(this.$route && this.$route.query)||{}
      if(query.tab!=='certifications')return false
      this.tab='certifications'
      this.query.auditStatus=['1','2','3'].includes(String(query.auditStatus))?Number(query.auditStatus):undefined
      this.query.keyword='';this.query.country='';this.query.companyName='';this.query.pageNum=1
      return true
    },
    scheduleTableHeight () {
      cancelAnimationFrame(this.tableHeightFrame)
      this.tableHeightFrame = requestAnimationFrame(this.updateTableHeight)
    },
    updateTableHeight () {
      const region = this.$refs.tableRegion
      const pager = this.$refs.pager && this.$refs.pager.$el
      if (!region || !pager || !region.getClientRects().length) return
      const header = region.querySelector('.ant-table-scroll .ant-table-header')
      if (!header) return
      const cardBody = this.$refs.memberCard.$el.querySelector('.ant-card-body')
      const pagerStyle = window.getComputedStyle(pager)
      // Keep pagination, card padding and a bottom page gutter in the viewport.
      // scroll.y includes the horizontal scrollbar; only the rows scroll vertically.
      const reserved = pager.offsetHeight + parseFloat(pagerStyle.marginTop || 0) +
        parseFloat(window.getComputedStyle(cardBody).paddingBottom || 0) + 26
      const height = Math.max(24, Math.floor(window.innerHeight - region.getBoundingClientRect().top - header.offsetHeight - reserved))
      if (height !== this.tableBodyHeight) this.tableBodyHeight = height
    },
    changeTab () { this.query = { keyword: '', country: '', companyName: '', auditStatus: undefined, sortBy: undefined, sortOrder: 'desc', pageNum: 1, pageSize: 20 }; this.load() },
    search () { this.query.pageNum = 1; this.load() },
    changePage (page, pageSize) { this.query.pageNum = page; this.query.pageSize = pageSize; this.load() },
    money (v) { return Number(v || 0).toFixed(2) },
    expiryDate (v) { return Number(v) > 0 ? moment.unix(Number(v)) : null },
    load () {
      this.loading = true
      let request
      if (this.tab === 'certifications') request = pageCertifications(this.query)
      else if (this.tab === 'cancellations') request = pageCancellations(this.query)
      else request = pageMembers(this.query)
      request.then(res => {
        this.rows = (res.data && res.data.rows) || []
        this.total = Number((res.data && res.data.total) || 0)
      }).finally(() => { this.loading = false })
    },
    toggleUser (record) {
      setMemberStatus(record.id, Number(record.status) === 1 ? 0 : 1).then(() => {
        this.$message.success('状态已更新')
        this.load()
      })
    },
    openUser (record) {
      getMember(record.id).then(res => {
        this.userDetail = res.data
        this.userVisible = true
      })
    },
    openAssign (record) {
      this.assignUserId = record.id
      this.assignServiceId = record.service_id || 0
      listServiceAgents({ pageNum: 1, pageSize: 200, status: 1, csc: 2 }).then(res => {
        const rows = (res.data && res.data.rows) || res.data || []
        this.agents = rows.filter(item => Number(item.csc) === 2 && Number(item.status) === 1)
        if (!this.agents.some(item => Number(item.id) === Number(this.assignServiceId))) this.assignServiceId = 0
        this.assignVisible = true
      })
    },
    saveAssign () {
      if (this.assignServiceId && !this.agents.some(item => Number(item.id) === Number(this.assignServiceId))) {
        return this.$message.warning('请选择有效的专属客服')
      }
      this.saving = true
      assignMemberService(this.assignUserId, this.assignServiceId || 0).then(() => {
        this.$message.success('专属客服已更新')
        this.assignVisible = false
        this.load()
      }).finally(() => { this.saving = false })
    },
    openBrochure (record) {
      this.brochureUserId = record.id
      this.brochureForm = {
        viewBrochure: Number(record.view_brochure) === 1,
        downloadBrochure: Number(record.download_brochure) === 1,
        viewPermanent: Number(record.view_brochure_permanent == null ? record.brochure_permanent : record.view_brochure_permanent) === 1,
        downloadPermanent: Number(record.download_brochure_permanent == null ? record.brochure_permanent : record.download_brochure_permanent) === 1,
        duration: Number(record.duration || 0),
        downloadDuration: Number(record.download_duration || 0)
      }
      this.brochureVisible = true
    },
    saveBrochure () {
      const now = moment().unix()
      if (this.brochureForm.viewBrochure && !this.brochureForm.viewPermanent && this.brochureForm.duration <= now) return this.$message.warning('请选择有效的查看到期时间')
      if (this.brochureForm.downloadBrochure && !this.brochureForm.downloadPermanent && this.brochureForm.downloadDuration <= now) return this.$message.warning('请选择有效的下载到期时间')
      this.saving = true
      setMemberBrochure(this.brochureUserId, this.brochureForm).then(() => {
        this.$message.success('画册权益已保存')
        this.brochureVisible = false
        this.load()
      }).finally(() => { this.saving = false })
    },
    openCert (aid) {
      getCertification(aid).then(res => {
        this.certDetail = res.data
        this.certForm = { ...res.data }
        this.certVisible = true
      })
    },
    saveCert () {
      this.saving = true
      updateCertification(this.certForm.aid, this.certForm).then(() => {
        this.$message.success('认证资料已保存')
        this.openCert(this.certForm.aid)
        this.load()
      }).finally(() => { this.saving = false })
    },
    audit (record, result) {
      if (result === 2) {
        auditCertification(record.aid, 2, '').then(() => {
          this.$message.success('审核通过')
          this.certVisible = false
          this.load()
        })
        return
      }
      this.$confirm({
        title: '驳回企业认证',
        content: '确认驳回该认证申请？',
        onOk: () => auditCertification(record.aid, 3, '资料不符合要求').then(() => {
          this.$message.success('已驳回')
          this.certVisible = false
          this.load()
        })
      })
    },
    previewImage (url) { this.previewUrl = url; this.previewVisible = true },
    // App 约定：1=审核中 2=已通过 3=已驳回
    auditText (value) {
      const v = Number(value)
      if (v === 2) return '已通过'
      if (v === 3) return '已驳回'
      return '待审核'
    },
    auditColor (value) {
      const v = Number(value)
      if (v === 2) return 'green'
      if (v === 3) return 'red'
      return 'orange'
    },
    certColor (record) {
      const t = record.cert_status_text || ''
      if (t.indexOf('通过') >= 0 || t.indexOf('已认证') >= 0) return 'green'
      if (t.indexOf('驳回') >= 0) return 'red'
      if (t.indexOf('待') >= 0) return 'orange'
      return 'orange'
    },
    brochureColor (record) {
      if (Number(record.download_brochure_valid) === 1) return 'green'
      if (Number(record.view_brochure_valid) === 1) return 'blue'
      return 'orange'
    }
  }
}
</script>

<style scoped>
.toolbar { margin-bottom: 16px; display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.member-table-region { min-width: 0; }
.member-table-region /deep/ .ant-table-body { overscroll-behavior: contain; }
.member-table-region /deep/ .ant-table-scroll > .ant-table-body::-webkit-scrollbar { width: auto; height: auto; }
.member-table-region /deep/ .ant-table-scroll > .ant-table-body::-webkit-scrollbar-track { background: #f0f0f0; }
.member-table-region /deep/ .ant-table-scroll > .ant-table-body::-webkit-scrollbar-thumb { background: #b3b3b3; border: 2px solid #f0f0f0; border-radius: 6px; }
.member-table-region /deep/ .ant-table-scroll > .ant-table-body::-webkit-scrollbar-thumb:hover { background: #8c8c8c; }
/* Pin cells in the original rows, avoiding width gaps from cloned fixed-column tables. */
.member-users-table /deep/ .member-identity-column,
.member-users-table /deep/ .member-operation-column { position: sticky; z-index: 2; background: #fff; }
.member-users-table /deep/ .member-identity-column { left: 0; }
.member-users-table /deep/ .member-operation-column { right: 0; }
.member-users-table /deep/ .ant-table-thead .member-identity-column,
.member-users-table /deep/ .ant-table-thead .member-operation-column { z-index: 3; background: #fafafa !important; }
.member-users-table /deep/ .ant-table-tbody > tr:hover > td { background: #f5f5f5 !important; }
.member-users-table /deep/ .ant-table-tbody > tr > td { overflow-wrap: anywhere; }
.member-users-table /deep/ .member-brochure-column .ant-tag { white-space: normal; overflow-wrap: anywhere; max-width: 100%; margin-right: 0; line-height: 20px; }
.member-users-table /deep/ .user > div { min-width: 0; }
.member-users-table /deep/ .user .ant-avatar { flex-shrink: 0; }
.pager { margin-top: 16px; text-align: right; }
.brochure-right-section + .brochure-right-section { border-top: 1px solid #f0f0f0; padding-top: 16px; }
.brochure-right-section h4 { margin-bottom: 16px; font-weight: 600; }
.user { display: flex; align-items: center; gap: 10px; }
.muted { color: #8c8c8c; font-size: 12px; margin-top: 2px; }
.img-card { border: 1px solid #f0f0f0; border-radius: 6px; padding: 8px; cursor: pointer; text-align: center; }
.img-card img { width: 100%; height: 140px; object-fit: cover; border-radius: 4px; }
</style>
